import type { ChangeEventHandler, FC, ReactElement, RefCallback } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { rem } from '../../styles/theme/layout';
import {
  Arrow,
  Container,
  fieldStyles,
  floatingStyles,
  invalidStyles,
  Label,
  PADDING_SIDE,
} from './common.styles';
import arrowSvg from './SelectField-arrow.svg';

const Select = styled.select`
  ${fieldStyles}
  padding: ${rem(22)} ${PADDING_SIDE} ${rem(14)} ${PADDING_SIDE};
  ${invalidStyles}
`;

const DummyLabel = styled(Label)`
  pointer-events: none;
  ${(props: LabelProps) => props.isFloating && floatingStyles}
`;

type Option = {
  value?: string | number;
  label?: string | null;
};

type SelectFieldProps = {
  isValid?: boolean;
  formLabel: string | ReactElement;
  options?: Option[];
  innerRef?: RefCallback<HTMLSelectElement>;
};

type LabelProps = {
  isFloating: boolean;
};

const SelectField: FC<
  SelectFieldProps & Omit<React.HTMLProps<HTMLSelectElement>, 'as'>
> = ({ options, formLabel, isValid, onChange, innerRef, name, ...props }) => {
  const [isLabelFloating, setIsLabelFloating] = useState(Boolean(props.value));
  const { value } = props;

  useEffect(() => {
    setIsLabelFloating(Boolean(value));
  }, [value]);

  const handleChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    (event) => {
      setIsLabelFloating(true);
      if (onChange) onChange(event);
    },
    [onChange],
  );

  return options ? (
    <Container>
      <Select
        /*
         * isValid is an optional prop so we check specifically against boolean
         */
        aria-invalid={isValid === false ? 'true' : 'false'}
        {...props}
        onChange={handleChange}
        ref={innerRef}
        name={name}
        id={name}
      >
        <option disabled />
        {options.map((option, idx) => (
          <option key={idx} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      <DummyLabel htmlFor={name} isFloating={isLabelFloating}>
        {formLabel}
      </DummyLabel>
      <Arrow src={arrowSvg} alt="" />
    </Container>
  ) : null;
};

export default SelectField;
